/**
 * Staff Curriculum Dashboard
 * 
 * Features:
 * - View all enrolled residents with progress
 * - Pending milestone review queue
 * - Approve/reject submissions with feedback
 * - Generate and send progress reports
 * - Issue certificates
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DictationInput from '../../components/DictationInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, CheckCircle2, Clock, AlertCircle, FileText, Award, 
  ChevronRight, Send, Eye, Download, BookOpen, BarChart3,
  User, RefreshCw, Check, X, Mail
} from 'lucide-react';

const PROGRAM_COLORS = {
  purpose_driven: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  our_square: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' }
};

export default function StaffCurriculumDashboard() {
  const { axiosAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [residents, setResidents] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedResident, setSelectedResident] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [certOpen, setCertOpen] = useState(false);
  const [certType, setCertType] = useState('phase');
  const [certPhase, setCertPhase] = useState('');
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = axiosAuth();
      const [residentsRes, pendingRes, dashRes] = await Promise.all([
        api.get('/curriculum/all-residents-progress'),
        api.get('/curriculum/pending-reviews'),
        api.get('/curriculum/staff-dashboard')
      ]);
      setResidents(residentsRes.data);
      setPendingReviews(pendingRes.data);
      setStats(dashRes.data.stats);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadReport = async (enrollmentId) => {
    try {
      const api = axiosAuth();
      const res = await api.get(`/curriculum/progress-report/${enrollmentId}`);
      setReportData(res.data);
      setSelectedResident(enrollmentId);
    } catch {
      toast.error('Failed to load report');
    }
  };

  const sendReport = async (enrollmentId) => {
    try {
      const api = axiosAuth();
      const res = await api.post(`/curriculum/progress-report/${enrollmentId}/send`);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send report');
    }
  };

  const handleReview = async (status) => {
    if (!selectedMilestone) return;
    try {
      const api = axiosAuth();
      await api.put(`/curriculum/milestones/${selectedMilestone.id}/review`, null, {
        params: { status, notes: reviewNotes }
      });
      toast.success(`Milestone ${status === 'approved' ? 'approved' : 'returned for revision'}`);
      setReviewOpen(false);
      setSelectedMilestone(null);
      setReviewNotes('');
      loadData();
    } catch {
      toast.error('Failed to review milestone');
    }
  };

  const issueCertificate = async (enrollmentId) => {
    try {
      const api = axiosAuth();
      await api.post('/curriculum/certificates', null, {
        params: {
          enrollment_id: enrollmentId,
          certificate_type: certType,
          phase_id: certType === 'phase' ? certPhase : null
        }
      });
      toast.success('Certificate issued!');
      setCertOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to issue certificate');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading curriculum dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-curriculum-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Curriculum Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage resident progress, review milestones, issue certificates</p>
        </div>
        <Button variant="outline" onClick={loadData} className="rounded-full gap-2">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:ring-2 hover:ring-teal-200 transition-all" onClick={() => setActiveTab('residents')}>
            <CardContent className="p-4 text-center">
              <Users size={24} className="mx-auto mb-2 text-teal-600" />
              <p className="text-2xl font-bold">{stats.total_enrolled}</p>
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">Total Enrolled <ChevronRight size={10} /></p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-xl bg-amber-50 cursor-pointer hover:ring-2 hover:ring-amber-200 transition-all" onClick={() => setActiveTab('reviews')}>
            <CardContent className="p-4 text-center">
              <Clock size={24} className="mx-auto mb-2 text-amber-600" />
              <p className="text-2xl font-bold text-amber-700">{stats.pending_reviews}</p>
              <p className="text-xs text-amber-600 flex items-center justify-center gap-1">Pending Reviews <ChevronRight size={10} /></p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-xl cursor-pointer hover:ring-2 hover:ring-emerald-200 transition-all" onClick={() => setActiveTab('overview')}>
            <CardContent className="p-4 text-center">
              <CheckCircle2 size={24} className="mx-auto mb-2 text-emerald-600" />
              <p className="text-2xl font-bold">{stats.total_milestones_completed}</p>
              <p className="text-xs text-muted-foreground">Milestones Completed</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm rounded-xl">
            <CardContent className="p-4 text-center">
              <div className="flex justify-center gap-2 mb-2">
                <Badge className="bg-teal-50 text-teal-700 border-0">{stats.purpose_driven_enrolled} PD</Badge>
                <Badge className="bg-cyan-50 text-cyan-700 border-0">{stats.our_square_enrolled} OS</Badge>
              </div>
              <p className="text-xs text-muted-foreground">By Program</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 h-auto p-1 bg-slate-100 rounded-xl max-w-md">
          <TabsTrigger value="overview" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <BarChart3 size={14} className="mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-lg text-xs py-2 data-[state=active]:bg-white relative">
            <Clock size={14} className="mr-1" /> Reviews
            {stats?.pending_reviews > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[10px] rounded-full flex items-center justify-center">
                {stats.pending_reviews}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="residents" className="rounded-lg text-xs py-2 data-[state=active]:bg-white">
            <Users size={14} className="mr-1" /> All Residents
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Top Performers */}
          <Card className="border-0 shadow-sm rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Award size={16} className="text-amber-500" /> Top Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {residents.slice(0, 5).map((r, i) => {
                  const colors = PROGRAM_COLORS[r.program_id] || PROGRAM_COLORS.purpose_driven;
                  return (
                    <div key={r.enrollment_id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{r.user_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge className={`${colors.bg} ${colors.text} border-0 text-xs`}>{r.program_name}</Badge>
                          <span className="text-xs text-muted-foreground">{r.current_phase}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-teal-600">{r.progress_percent}%</p>
                        <p className="text-xs text-muted-foreground">{r.completed}/{r.total}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Needs Attention */}
          {residents.filter(r => r.pending > 3 || r.progress_percent < 10).length > 0 && (
            <Card className="border-0 shadow-sm rounded-xl border-l-4 border-l-amber-400">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-500" /> Needs Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {residents.filter(r => r.pending > 3 || r.progress_percent < 10).slice(0, 5).map(r => (
                    <div key={r.enrollment_id} className="flex items-center justify-between p-2 rounded-lg bg-amber-50">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-amber-600" />
                        <span className="text-sm font-medium">{r.user_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.pending > 3 && <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">{r.pending} pending</Badge>}
                        {r.progress_percent < 10 && <Badge className="bg-red-100 text-red-700 border-0 text-xs">{r.progress_percent}% progress</Badge>}
                        <Button size="sm" variant="ghost" onClick={() => loadReport(r.enrollment_id)} className="h-7">
                          <Eye size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-4 mt-4">
          <Card className="border-0 shadow-sm rounded-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Pending Milestone Reviews ({pendingReviews.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {pendingReviews.length > 0 ? (
                <div className="space-y-3">
                  {pendingReviews.map(m => (
                    <div key={m.id} className="p-4 rounded-xl border border-slate-100 bg-white">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-amber-50 text-amber-700 border-0 text-xs">
                              <Clock size={10} className="mr-1" /> Pending
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {new Date(m.submitted_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="font-medium text-sm">{m.user_name}</p>
                          {m.task_info && (
                            <div className="mt-2 p-2 rounded-lg bg-slate-50">
                              <p className="text-xs text-muted-foreground">{m.task_info.category}</p>
                              <p className="text-sm font-medium">{m.task_info.name}</p>
                            </div>
                          )}
                          {m.submission_text && (
                            <p className="text-sm text-muted-foreground mt-2 italic">"{m.submission_text}"</p>
                          )}
                          {m.submission_file && (
                            <p className="text-xs text-cyan-600 mt-1">📎 File attached: {m.submission_file}</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Dialog open={reviewOpen && selectedMilestone?.id === m.id} onOpenChange={(open) => { setReviewOpen(open); if (!open) setSelectedMilestone(null); }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                className="rounded-full bg-teal-600 hover:bg-teal-700"
                                onClick={() => { setSelectedMilestone(m); setReviewOpen(true); }}
                              >
                                Review
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-2xl">
                              <DialogHeader>
                                <DialogTitle>Review Milestone</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 pt-2">
                                <div className="p-3 rounded-xl bg-slate-50">
                                  <p className="text-xs text-muted-foreground">Resident</p>
                                  <p className="font-medium">{m.user_name}</p>
                                  {m.task_info && (
                                    <>
                                      <p className="text-xs text-muted-foreground mt-2">Task</p>
                                      <p className="text-sm">{m.task_info.name}</p>
                                    </>
                                  )}
                                </div>
                                {m.submission_text && (
                                  <div className="p-3 rounded-xl border border-slate-200">
                                    <p className="text-xs text-muted-foreground mb-1">Submission</p>
                                    <p className="text-sm">{m.submission_text}</p>
                                    {m.original_text && m.original_text !== m.submission_text && (
                                      <details className="mt-2">
                                        <summary className="text-xs text-amber-600 cursor-pointer hover:text-amber-700">View Original Dictation</summary>
                                        <p className="text-sm text-slate-500 mt-1 p-2 bg-amber-50 rounded-lg italic">"{m.original_text}"</p>
                                      </details>
                                    )}
                                  </div>
                                )}
                                <div className="space-y-1.5">
                                  <Label>Feedback Notes</Label>
                                  <DictationInput 
                                    value={reviewNotes}
                                    onChange={(val) => setReviewNotes(val)}
                                    placeholder="Add feedback for the resident..."
                                    rows={3}
                                  showCleanup={false} />
                                </div>
                                <div className="flex gap-2">
                                  <Button 
                                    onClick={() => handleReview('approved')}
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-full gap-1"
                                  >
                                    <Check size={14} /> Approve
                                  </Button>
                                  <Button 
                                    onClick={() => handleReview('revision_needed')}
                                    variant="outline"
                                    className="flex-1 rounded-full gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                                  >
                                    <X size={14} /> Request Revision
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 size={48} className="mx-auto text-emerald-300 mb-3" />
                  <p className="font-medium text-slate-900">All caught up!</p>
                  <p className="text-sm text-muted-foreground">No pending reviews</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Residents Tab */}
        <TabsContent value="residents" className="space-y-4 mt-4">
          <div className="space-y-3">
            {residents.map(r => {
              const colors = PROGRAM_COLORS[r.program_id] || PROGRAM_COLORS.purpose_driven;
              return (
                <Card key={r.enrollment_id} className="border-0 shadow-sm rounded-xl hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
                        <User size={24} className="text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900">{r.user_name}</p>
                          <Badge className={`${colors.bg} ${colors.text} border-0 text-xs`}>{r.program_name}</Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-muted-foreground">Phase: {r.current_phase}</span>
                          {r.peer_specialist && <span className="text-xs text-cyan-600">Peer: {r.peer_specialist}</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Progress value={r.progress_percent} className="h-1.5 flex-1" />
                          <span className="text-xs font-medium">{r.progress_percent}%</span>
                        </div>
                        <div className="flex gap-2 mt-1">
                          <Badge className="bg-emerald-50 text-emerald-700 border-0 text-xs">{r.completed} complete</Badge>
                          {r.pending > 0 && <Badge className="bg-amber-50 text-amber-700 border-0 text-xs">{r.pending} pending</Badge>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => loadReport(r.enrollment_id)}>
                          <Eye size={12} /> Report
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => sendReport(r.enrollment_id)}>
                          <Mail size={12} /> Send
                        </Button>
                        <Dialog open={certOpen && selectedResident === r.enrollment_id} onOpenChange={(open) => { setCertOpen(open); if (open) setSelectedResident(r.enrollment_id); }}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="rounded-full gap-1">
                              <Award size={12} /> Certificate
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="rounded-2xl">
                            <DialogHeader>
                              <DialogTitle>Issue Certificate</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-4 pt-2">
                              <p className="text-sm text-muted-foreground">Issue certificate to {r.user_name}</p>
                              <div className="space-y-1.5">
                                <Label>Certificate Type</Label>
                                <Select value={certType} onValueChange={setCertType}>
                                  <SelectTrigger><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="phase">Phase Completion</SelectItem>
                                    <SelectItem value="program">Program Completion</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              {certType === 'phase' && (
                                <div className="space-y-1.5">
                                  <Label>Phase</Label>
                                  <Select value={certPhase} onValueChange={setCertPhase}>
                                    <SelectTrigger><SelectValue placeholder="Select phase" /></SelectTrigger>
                                    <SelectContent>
                                      {r.program_id === 'purpose_driven' ? (
                                        <>
                                          <SelectItem value="pd_awareness">Awareness</SelectItem>
                                          <SelectItem value="pd_responsibility">Responsibility</SelectItem>
                                          <SelectItem value="pd_rebuilding">Rebuilding</SelectItem>
                                          <SelectItem value="pd_leadership">Leadership</SelectItem>
                                        </>
                                      ) : (
                                        <>
                                          <SelectItem value="os_stabilization">Stabilization</SelectItem>
                                          <SelectItem value="os_recovery">Recovery</SelectItem>
                                          <SelectItem value="os_responsibility">Responsibility</SelectItem>
                                          <SelectItem value="os_independence">Independence</SelectItem>
                                        </>
                                      )}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                              <Button 
                                onClick={() => issueCertificate(r.enrollment_id)}
                                className="w-full bg-amber-500 hover:bg-amber-600 rounded-full gap-1"
                              >
                                <Award size={14} /> Issue Certificate
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Progress Report Modal */}
      <Dialog open={!!reportData} onOpenChange={(open) => { if (!open) setReportData(null); }}>
        <DialogContent className="rounded-2xl max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Progress Report</DialogTitle>
          </DialogHeader>
          {reportData && (
            <div className="space-y-4 pt-2">
              {/* Header */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50">
                <div>
                  <p className="font-bold text-lg">{reportData.resident.name}</p>
                  <p className="text-sm text-muted-foreground">{reportData.program.name} - {reportData.current_phase.name}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-teal-600">{reportData.summary.overall_progress}%</p>
                  <p className="text-xs text-muted-foreground">Overall Progress</p>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-2">
                <div className="p-3 rounded-xl bg-emerald-50 text-center">
                  <p className="text-lg font-bold text-emerald-700">{reportData.summary.completed}</p>
                  <p className="text-xs text-emerald-600">Completed</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50 text-center">
                  <p className="text-lg font-bold text-amber-700">{reportData.summary.pending}</p>
                  <p className="text-xs text-amber-600">Pending</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 text-center">
                  <p className="text-lg font-bold text-slate-700">{reportData.summary.not_started}</p>
                  <p className="text-xs text-muted-foreground">Remaining</p>
                </div>
                <div className="p-3 rounded-xl bg-cyan-50 text-center">
                  <p className="text-lg font-bold text-cyan-700">{reportData.summary.completed_this_week}</p>
                  <p className="text-xs text-cyan-600">This Week</p>
                </div>
              </div>

              {/* Concerns */}
              {reportData.concerns.length > 0 && (
                <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="text-sm font-medium text-amber-800 mb-2">⚠️ Attention Needed</p>
                  <ul className="space-y-1">
                    {reportData.concerns.map((c, i) => (
                      <li key={`item-${i}`} className="text-sm text-amber-700">• {c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Class Details */}
              <div>
                <p className="font-medium text-sm mb-2">Progress by Category</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {reportData.class_details.map(cls => (
                    <div key={cls.category} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{cls.category}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Progress value={cls.progress_percent} className="h-1 flex-1" />
                          <span className="text-xs text-muted-foreground">{cls.completed}/{cls.total}</span>
                        </div>
                      </div>
                      <span className="text-sm font-bold text-teal-600">{cls.progress_percent}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button 
                  onClick={() => sendReport(selectedResident)}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 rounded-full gap-1"
                >
                  <Send size={14} /> Email to Peer Specialist
                </Button>
                <Button 
                  variant="outline"
                  className="rounded-full gap-1"
                  onClick={() => window.print()}
                >
                  <Download size={14} /> Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
